import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useDisclosure } from '@reactuses/core';
import { GameHeater } from './_components/HeaterGameLogic';
import { GameHeader } from '@/components/GameHeader';
import {
  DefaultPopup,
  DefaultPopupProps,
} from '@/components/popups/DefaultPopup';
import { StartGamePopup } from '@/components/popups/StartGamePopup';
import { assets } from '@/lib/assets';
import { useBrowserLeaveActions } from '@/lib/hooks/useBrowserLeaveActions';
import { cn } from '@/lib/utils';
import { playSound, stopSound } from '@/lib/utils/playSound';
import { waitForSeconds } from '@/lib/utils/waitForSeconds';
import { gameDataContext } from '@/providers/GameDataProvider';

// Game status type
type GameStatus = 'idle' | 'playing' | 'paused' | 'gameover';

export const HeaterGameContent = () => {
  const wrapper = useRef<HTMLCanvasElement | null>(null);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    nextGamePath,
    setCurrentGameScore,
    openProgressPopup,
    progressPopupVisible,
  } = useContext(gameDataContext);
  const [gameStarted, setGameStarted] = useState(false);
  const [temperature, setTemperature] = useState(50); // 0-100 scale: 0=cold, 100=hot
  const [assetsLoaded, setAssetsLoaded] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60); // TODO: get it from GameHeaterLogic
  const [skipPopupOpen, setSkipPopupOpen] = useState(false);

  // on brower leave or tab change, we reset the game
  useBrowserLeaveActions({
    onLeaveCallback: () => {
      setCurrentGameScore(0);
    },
  });

  const {
    isOpen: startPopupOpen,
    onClose: close_startPopup,
    onOpen: open_startPopup,
  } = useDisclosure();

  const [infoPopupData, setInfoPopupData] = useState<DefaultPopupProps>({
    title: '',
    description: '',
    buttonLabel: '',
    onButtonClick: () => {
      console.log('continue function not set');
    },
  });
  const {
    isOpen: infoPopup_open,
    onClose: close_infoPopup,
    onOpen: open_infoPopup,
  } = useDisclosure();

  // --------------------- methods --------------------------------

  // Calculate the opacity for temperature-based backgrounds
  const getFrozenOpacity = () => {
    if (temperature > 50) return 0;

    // We now have multiple levels of freezing
    if (temperature <= GameHeater.TEMPERATURE_EXTREMELY_COLD) {
      // Extremely frozen - maximum opacity
      return 0.8;
    } else if (temperature <= GameHeater.TEMPERATURE_COLD) {
      // Normal frozen - interpolate between TEMPERATURE_COLD and TEMPERATURE_EXTREMELY_COLD
      return (
        0.4 +
        0.4 *
          ((GameHeater.TEMPERATURE_COLD - temperature) /
            (GameHeater.TEMPERATURE_COLD -
              GameHeater.TEMPERATURE_EXTREMELY_COLD))
      );
    } else {
      // Starting to freeze - interpolate between 50 and TEMPERATURE_COLD
      return 0.4 * ((50 - temperature) / (50 - GameHeater.TEMPERATURE_COLD));
    }
  };

  // Calculate the opacity for frozen_max background
  const getFrozenMaxOpacity = () => {
    if (temperature > GameHeater.TEMPERATURE_EXTREMELY_COLD) return 0;

    // Only show when extremely cold (below TEMPERATURE_EXTREMELY_COLD)
    // Gradually fade in as temperature decreases
    const baseOpacity = Math.min(
      1,
      (GameHeater.TEMPERATURE_EXTREMELY_COLD - temperature) / 10
    );
    return baseOpacity * 0.9; // Max opacity 90%
  };

  const getBurnOpacity = () => {
    if (temperature < 50) return 0;

    // Reach maximum opacity at 60% of the way between 50 and 100
    // So at temperature = 80 (50 + 50*0.6), opacity is at 100%
    const opacity =
      ((temperature - 50) / (50 - GameHeater.TEMPERATURE_HOT)) * 0.8;

    // Limit to 0.8 (80% maximum opacity)
    return Math.min(0.8, Math.abs(opacity));
  };

  // Get thermometer color based on temperature
  const getThermometerColor = () => {
    if (temperature <= GameHeater.TEMPERATURE_EXTREMELY_COLD) {
      return 'bg-blue-600'; // Dark blue for extremely cold
    } else if (temperature <= GameHeater.TEMPERATURE_COLD) {
      return 'bg-blue-400'; // Light blue for cold
    } else if (temperature >= GameHeater.TEMPERATURE_HOT) {
      return 'bg-red-600'; // Red for hot
    } else if (temperature >= 45 && temperature <= 65) {
      return 'bg-green-500'; // Green for balanced
    } else if (temperature > 65) {
      // Between balanced and hot
      return 'bg-red-400'; // Light red
    } else {
      // Between balanced and cold (below 45)
      return 'bg-blue-300'; // Very light blue
    }
  };

  const showInfoPopup = (data: DefaultPopupProps) => {
    setInfoPopupData(data);
    open_infoPopup();
  };

  const retry = () => {
    setCurrentGameScore(0);
    close_infoPopup();
    // Stop any existing music and restart it when retrying
    stopSound(assets.game_heater.sounds.music);
    playSound(assets.game_heater.sounds.music, {
      loop: true,
      volume: 0.5,
    });
    open_startPopup();
  };

  const handleStartGame = () => {
    // Only start game when all assets are loaded
    if (assetsLoaded) {
      GameHeater.startGame();
      setGameStarted(true);
    } else {
      console.warn('Cannot start game: assets not fully loaded');
    }
  };

  // show game over popup
  const showGameOverPopup = async (
    reason: 'frozen' | 'burned' | 'timeout',
    success: boolean,
    callBack: () => void
  ) => {
    const titleString =
      reason === 'burned'
        ? t('game_heater.burned.title')
        : reason === 'frozen'
          ? t('game_heater.frozen.title')
          : t('game_heater.timeup.title');

    const descriptionString =
      reason === 'burned'
        ? t('game_heater.burned.description')
        : reason === 'frozen'
          ? t('game_heater.frozen.description')
          : t('game_heater.timeup.description');

    let title;
    if (reason !== 'timeout') {
      title = (
        <div className="-mb-4 text-4xl font-bold">
          <img
            src={
              reason === 'burned'
                ? assets.game_heater.images.heater_burned
                : assets.game_heater.images.heater_frozen
            }
            alt="popup header"
            className="-mt-32 h-auto max-h-52 w-full object-contain"
          />
          {titleString}
        </div>
      );
    }
    // game is completed, we add the item and wait a bit for animation to complete
    await waitForSeconds(1);
    const button = success ? t('popup.success.button') : t('common.retry');
    showInfoPopup({
      title: title || titleString,
      description: descriptionString,
      buttonLabel: button,
      onButtonClick: callBack,
      ...(success
        ? {
            cancelLabel: t('popup.success.retry'),
            onCancel() {
              retry();
            },
          }
        : {}),
    });
  };

  const onGameFailed = (reason: 'frozen' | 'burned' | 'timeout') => {
    stopSound(assets.game_heater.sounds.music);
    playSound(assets.game_heater.sounds.fail);
    showGameOverPopup(reason, false, () => {
      retry();
    });
  };
  const onGameSuccess = async (reason: 'frozen' | 'burned' | 'timeout') => {
    stopSound(assets.game_heater.sounds.music);
    playSound(assets.game_heater.sounds.fail);
    showGameOverPopup(reason, true, () => {
      navigate(nextGamePath);
    });
  };

  // --------------------- effects ------------------------

  // Watch popup state changes to pause/resume game
  useEffect(() => {
    const anyPopupOpen =
      startPopupOpen || infoPopup_open || skipPopupOpen || progressPopupVisible;

    if (anyPopupOpen) GameHeater.pauseGame();
    else GameHeater.resumeGame();
  }, [startPopupOpen, infoPopup_open, skipPopupOpen, progressPopupVisible]);

  // Preload all necessary assets
  useEffect(() => {
    const imagesToPreload = [
      assets.game_heater.images.bg_normal,
      assets.game_heater.images.bg_frozen,
      assets.game_heater.images.bg_frozen_max,
      assets.game_heater.images.bg_fire,
      assets.game_heater.images.heater_frozen,
      assets.game_heater.images.heater_burned,
      assets.game_heater.images.button_hot,
    ];

    let loadedCount = 0;
    const totalImages = imagesToPreload.length;

    const preloadImage = (src: string) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = src;
        img.onload = () => {
          loadedCount++;
          console.log(`Preloaded image ${loadedCount}/${totalImages}: ${src}`);
          resolve(true);
        };
        img.onerror = (err) => {
          console.error(`Failed to preload image: ${src}`, err);
          reject(err);
        };
      });
    };

    // Wait for all assets to load
    Promise.all([
      ...imagesToPreload.map((src) => preloadImage(src)),
      // If you need to preload SVGs, use fetch:
      // fetch(assets.game_heater.images.flame_svg).then(res => res.text())
    ])
      .then(() => {
        console.log('All assets preloaded successfully');
        setAssetsLoaded(true);
      })
      .catch((error) => {
        console.error('Error preloading assets:', error);
        // Set assets as loaded even if some fail, to not block the game
        setAssetsLoaded(true);
      });
  }, []);

  // effect to initialize app
  // Track if game is initialized to prevent multiple initializations
  const isInitialized = useRef(false);
  useEffect(() => {
    if (wrapper.current) {
      isInitialized.current = true;
      GameHeater.initApp(wrapper.current);
    }
    return () => {
      if (isInitialized.current) {
        GameHeater.destroyApp();
        isInitialized.current = false;
      }
    };
  }, [navigate]);

  // effect to update callbacks
  useEffect(() => {
    GameHeater.updateCallbacks({
      onGameStarted: () => {
        close_startPopup();
      },
      onScoreUpdated: (newScore: number) => {
        setCurrentGameScore(newScore);
      },
      onTemperatureChanged: (newTemp: number) => setTemperature(newTemp),
      onTimeUpdated: (newTimeLeft: number) => setTimeLeft(newTimeLeft),
      onGameOver: (
        finalScore: number,
        reason: 'frozen' | 'burned' | 'timeout'
      ) => {
        setCurrentGameScore(finalScore);
        if (finalScore > 0) {
          onGameSuccess(reason);
        } else {
          onGameFailed(reason);
        }
      },
      onStatusChanged: (newStatus: GameStatus) => {
        console.log('status changed:', newStatus);
      },
    });
  }, [
    close_startPopup,
    setCurrentGameScore,
    onGameSuccess,
    onGameFailed,
    navigate,
    nextGamePath,
  ]);

  // Separate useEffect for opening start popup
  useEffect(() => {
    const timer = setTimeout(() => {
      // Only show start popup when assets are loaded
      if (assetsLoaded) {
        openProgressPopup(() => {
          open_startPopup();
        });
      } else {
        // If assets aren't loaded yet, set up a watcher
        const checkInterval = setInterval(() => {
          if (assetsLoaded) {
            clearInterval(checkInterval);
            openProgressPopup(() => {
              open_startPopup();
            });
          }
        }, 500);

        // Clean up interval if component unmounts
        return () => clearInterval(checkInterval);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [assetsLoaded]); // Add assetsLoaded as dependency

  // start music
  useEffect(() => {
    playSound(assets.game_heater.sounds.music, {
      loop: true,
      volume: 0.5,
    });
    // unload
    return () => {
      stopSound(assets.game_heater.sounds.music, 1000, true);
    };
  }, []);

  // --------------------- render ------------------------

  return (
    <>
      {/* // --------------------- Header ------------------------ */}
      <GameHeader
        open
        timeInSec={timeLeft}
        onSkipButtonClick={() => setSkipPopupOpen(true)}
        onSkipCancel={() => setSkipPopupOpen(false)}
      />

      {/* // --------------------- game ------------------------ */}
      <div className="absolute left-0 top-0 flex h-screen w-screen justify-center overflow-hidden bg-black">
        {/* Base background */}
        <img
          alt="game background"
          src={assets.game_heater.images.bg_normal}
          className={cn(
            'absolute h-full w-auto object-cover',
            !gameStarted && 'opacity-60 blur-md filter'
          )}
        />

        {/* Frozen overlay */}
        <img
          alt="frozen background"
          src={assets.game_heater.images.bg_frozen}
          className="absolute h-full w-auto object-cover transition-opacity duration-500"
          style={{ opacity: getFrozenOpacity() }}
        />

        {/* Frozen MAX overlay - new extreme cold background */}
        <img
          alt="extremely frozen background"
          src={assets.game_heater.images.bg_frozen_max}
          className="absolute h-full w-auto object-cover transition-opacity duration-500"
          style={{ opacity: getFrozenMaxOpacity() }}
        />

        {/* Burn overlay */}
        <img
          alt="burn background"
          src={assets.game_heater.images.bg_fire}
          className="absolute h-full w-auto object-cover transition-opacity duration-500"
          style={{ opacity: getBurnOpacity() }}
        />

        {/* Game canvas */}
        <canvas className="absolute h-full w-full" ref={wrapper} />

        {/* Vertical Temperature Thermometer - Only show when game has started */}
        {gameStarted && (
          <div className="pointer-events-none absolute left-1/2 top-28 -translate-x-1/2 -translate-y-20 transform rounded-2xl bg-primary/50 px-12 pb-10 pt-20 md:px-16">
            {/* Thermometer container */}
            <div className="flex flex-col items-center">
              {/* Vertical thermometer with glass tube effect */}
              <div
                className="relative h-52 w-6 rounded-full bg-gray-200 shadow-inner"
                style={{
                  border: '2px solid white',
                  boxShadow:
                    'inset 0 0 10px rgba(0,0,0,0.2), 0 0 10px rgba(255,255,255,0.5)',
                }}
              >
                {/* Mercury/liquid fill - dynamically sized based on temperature */}
                <div
                  className={`absolute bottom-0 left-0 w-full rounded-full transition-all duration-300 ${getThermometerColor()}`}
                  style={{
                    height: `${temperature}%`,
                    boxShadow: 'inset 0 0 5px rgba(255,255,255,0.5)',
                  }}
                />

                {/* Graduation marks - left side */}
                <div className="absolute -left-5 top-0 flex h-full flex-col justify-between py-8">
                  {[...Array(7)].map((_, i) => (
                    <div
                      key={`left-${i}`}
                      className={cn(
                        'h-0.5 w-3 rounded-full bg-white',
                        i % 2 === 1 && '-ml-1 h-1 w-4'
                      )}
                    />
                  ))}
                </div>

                {/* Graduation marks - right side */}
                <div className="absolute -right-5 top-0 flex h-full flex-col justify-between py-8">
                  {[...Array(7)].map((_, i) => (
                    <div
                      key={`right-${i}`}
                      className={cn(
                        'h-0.5 w-3 rounded-full bg-white',
                        i % 2 === 1 && '-mr-1 h-1 w-4'
                      )}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* warning text */}
        <div
          className={cn(
            'pointer-events-none absolute rounded-3xl bg-primary/50 px-4 py-1',
            'left-1/2 top-[340px] z-20 -translate-x-1/2 transform',
            'text-center text-lg font-bold text-white md:text-xl',
            'duration-400 transition-all ease-in-out',
            getThermometerColor()
          )}
          style={{
            opacity:
              temperature <= GameHeater.TEMPERATURE_COLD ||
              temperature >= GameHeater.TEMPERATURE_HOT
                ? 1
                : 0,
            visibility:
              temperature <= GameHeater.TEMPERATURE_COLD ||
              temperature >= GameHeater.TEMPERATURE_HOT
                ? 'visible'
                : 'hidden',

            backgroundColor: getThermometerColor(),
          }}
        >
          {temperature <= GameHeater.TEMPERATURE_EXTREMELY_COLD &&
            t('game_heater.warning.freezing')}
          {temperature > GameHeater.TEMPERATURE_EXTREMELY_COLD &&
            temperature <= GameHeater.TEMPERATURE_COLD &&
            t('game_heater.warning.too_cold')}
          {temperature >= GameHeater.TEMPERATURE_HOT &&
            t('game_heater.warning.too_hot')}
        </div>
      </div>

      {/* // --------------------- POPUPS ------------------------ */}
      <DefaultPopup
        isOpen={infoPopup_open}
        onClose={close_infoPopup}
        {...infoPopupData}
      />

      <StartGamePopup
        isOpen={startPopupOpen}
        onClose={handleStartGame}
        title={t('game_heater.title')}
        description={t('game_heater.description')}
      />

      {/* Loading indicator shown when assets are still loading */}
      {!assetsLoaded && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80">
          <div className="text-center">
            <div className="mb-4 h-16 w-16 animate-spin rounded-full border-b-2 border-t-2 border-white"></div>
            <p className="text-white">{t('loading')}</p>
          </div>
        </div>
      )}
    </>
  );
};

// Wrap the game content with our error boundary
export const PageHeaterGame = () => {
  return <HeaterGameContent />;
};
